import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    VStack,
    Image,
    Text,
    Skeleton,
    Button,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../utils/cloudinary-utils'
import {yieldToMain} from '../../../utils/common-utils'
import {
    isLinkedPdpVariant,
    isMetalSwatchAvailable,
    resolveSwatchVariant
} from '../../../utils/product-swatch-utils'
import SwatchGroup from '../../swatch-group'
import {ErrorIcon} from '../../icons'
import Swatch from '../../swatch-group/swatch'
import CharmSwatchContent from '../../swatch-group/charm-swatch-content'
import {useHistory} from 'react-router-dom'
import LoadingSpinner from '../../loading-spinner'

const isCharmTypeAttribute = (id) => id?.toLowerCase()?.replace(/[^a-z0-9]/g, '') === 'charmtype'

const getVariationValue = (value) => (typeof value === 'object' ? value?.value : value)

const getSelectedVariationValue = (variationValues, id) =>
    isCharmTypeAttribute(id)
        ? Object.entries(variationValues).find(([key]) => isCharmTypeAttribute(key))?.[1]
        : variationValues[id]

export const getCharmSwatchImage = (imageGroups = [], variationValues = {}) =>
    imageGroups
        .filter(({viewType, variationAttributes = []}) => {
            if (viewType !== 'swatch') return false

            const hasCharmType = variationAttributes.some(({id}) => isCharmTypeAttribute(id))

            return (
                hasCharmType &&
                variationAttributes.every(({id, values = []}) =>
                    values.some(
                        (value) =>
                            getVariationValue(value) ===
                            getSelectedVariationValue(variationValues, id)
                    )
                )
            )
        })
        .sort(
            (a, b) => (b.variationAttributes?.length || 0) - (a.variationAttributes?.length || 0)
        )?.[0]?.images?.[0]

const ActionSpinner = forwardRef((_, ref) => {
    const [isVisible, setIsVisible] = useState(false)

    useImperativeHandle(ref, () => ({
        show: () => setIsVisible(true),
        hide: () => setIsVisible(false)
    }))

    return isVisible ? <LoadingSpinner position="fixed" top="auto" /> : null
})

ActionSpinner.displayName = 'ActionSpinner'

const ProductViewSwatches = ({
    product,
    showLoading,
    productVariationAttributes,
    setSelectedCarat,
    isEngagementProduct,
    setProduct,
    setEngraveSwatchSpinner,
    styles,
    showOptionsMessage,
    onOpenSizeGuide,
    ispdp,
    isEditEngraving,
    isProductPartOfSet,
    driveRecipeID,
    variant,
    selectedCarat,
    isCart,
    setShouldFetchProduct,
    suppressSwatchSpinner = false
}) => {
    const intl = useIntl()
    const history = useHistory()
    const actionSpinnerRef = useRef(null)

    return (
        <VStack className="product-view-swatches" align="stretch" spacing={'30px'}>
            <ActionSpinner ref={actionSpinnerRef} />
            {showLoading ? (
                <>
                    {/* First Attribute Skeleton */}
                    <Skeleton height={6} width={32} />
                    <Skeleton height={20} width={64} />

                    {/* Second Attribute Skeleton */}
                    <Skeleton height={6} width={32} />
                    <Skeleton height={20} width={64} />
                </>
            ) : (
                productVariationAttributes
                    ?.filter(({values}) => values?.length)
                    // Filter out swatches that do not correspond to a linked variant.
                    // Metal ignores color (primary attr) but still honors size/length/etc.
                    ?.map((attr) => ({
                        ...attr,
                        values: attr?.values?.filter((value) => {
                            if (attr.id === 'metal') {
                                const metalAttributeKey = attr.originalId || attr.id
                                const currentValues =
                                    variant?.variationValues || product?.variationValues || {}
                                return isMetalSwatchAvailable(
                                    product,
                                    metalAttributeKey,
                                    value.value,
                                    currentValues
                                )
                            }

                            const params = Object.fromEntries(
                                new URLSearchParams(value?.href?.split('?')?.[1] || '')
                            )

                            const selectedVariant =
                                product?.variants?.find((candidate) =>
                                    Object.entries(params)?.every(
                                        ([key, val]) => candidate?.variationValues?.[key] === val
                                    )
                                ) || false
                            return isLinkedPdpVariant(product, selectedVariant)
                        })
                    }))
                    ?.filter((attr) => attr.values.length)
                    ?.map(({id, originalId, selectedValue, values, name}, attrIndex) => {
                        const isCharmAttribute = isCharmTypeAttribute(originalId || id)
                        const swatches = values.map(
                            ({href, name, image, value, orderable}, index) => {
                                const params = Object.fromEntries(
                                    new URLSearchParams(href?.split('?')?.[1] || '')
                                )
                                const hasSelection = Boolean(selectedValue?.value)
                                const isSelected = selectedValue?.value === value
                                const charmImage = isCharmAttribute
                                    ? getCharmSwatchImage(product?.imageGroups, {
                                          ...(product?.variationValues || {}),
                                          ...(variant?.variationValues || {}),
                                          ...params,
                                          [originalId || id]: value
                                      })
                                    : null
                                const content = isCharmAttribute ? (
                                    <CharmSwatchContent
                                        image={charmImage}
                                        isSoldOut={!orderable}
                                        name={name}
                                    />
                                ) : image && id !== 'letter' ? (
                                    <Box
                                        height="100%"
                                        width="100%"
                                        className="swatch-box"
                                        minWidth="25px"
                                        backgroundRepeat="no-repeat"
                                        backgroundSize="cover"
                                        style={{
                                            backgroundImage: `url('${
                                                image.disBaseLink || image.link
                                            }')`
                                        }} // Wrap-up backgroundImage to fix the issues caused by ",".
                                    />
                                ) : (
                                    name
                                )
                                const isFirst = index === 0
                                // To mimic the behavior of a native radio input, only
                                // one swatch should receive tab focus; the rest can be
                                // selected using arrow keys when the swatch group has
                                // focus. The focused element is the selected option or
                                // the first in the group, if no option is selected.
                                // This is a slight difference, for simplicity, from the
                                // native element, where the first element is focused on
                                // `Tab` and the _last_ element is focused on `Shift+Tab`
                                const isFocusable = isSelected || (!hasSelection && isFirst)

                                if (
                                    (hasSelection || isSelected) &&
                                    id === 'carat' &&
                                    !selectedCarat &&
                                    isEngagementProduct
                                ) {
                                    setSelectedCarat(true)
                                }
                                return (
                                    <Swatch
                                        key={value}
                                        href={href}
                                        disabled={
                                            isEngagementProduct || id === 'metal'
                                                ? false
                                                : !orderable
                                        }
                                        value={value}
                                        id={id}
                                        name={name}
                                        product={product}
                                        isCart={isCart}
                                        ispdp={ispdp}
                                        variant={
                                            isCharmAttribute
                                                ? 'charm'
                                                : id === 'size' ||
                                                  id === 'letter' ||
                                                  id === 'carat' ||
                                                  id === 'length'
                                                ? 'square'
                                                : 'circle'
                                        }
                                        selected={isSelected}
                                        ariaLabel={
                                            isCharmAttribute
                                                ? `${name}${!orderable ? ', sold out' : ''}${
                                                      isSelected ? ', selected' : ''
                                                  }`
                                                : undefined
                                        }
                                        isFocusable={isFocusable}
                                        attributeId={originalId || id}
                                        swatchProps={{
                                            ...(id === 'carat'
                                                ? {
                                                      paddingLeft: '18px',
                                                      paddingRight: '18px',
                                                      lineHeight: '25px',
                                                      letterSpacing: '0.08px'
                                                  }
                                                : {})
                                        }}
                                        isCustomSwatch
                                        customSwatchSelect={async (variationParams) => {
                                            if (!suppressSwatchSpinner) {
                                                actionSpinnerRef.current?.show()
                                            }
                                            try {
                                                await yieldToMain()
                                                const selectedVariant = resolveSwatchVariant({
                                                    product,
                                                    attributeId: id,
                                                    attributeKey: originalId || id,
                                                    variationParams,
                                                    currentValues: variant?.variationValues || {}
                                                })

                                                if (!selectedVariant?.productId) {
                                                    return
                                                }

                                                setProduct({
                                                    ...product,
                                                    ...selectedVariant
                                                })

                                                if (isCart) {
                                                    setShouldFetchProduct(true)
                                                }
                                                if (ispdp) {
                                                    localStorage.setItem('changingSwatch', true)
                                                }

                                                if (!isCart) {
                                                    const urlHash = window.location.hash
                                                    let newVariantUrl = product?.c_allPdpLinks.find(
                                                        (p) => p.id === selectedVariant?.productId
                                                    )?.url

                                                    const updatedPath = (
                                                        window.location.pathname +
                                                        window.location.search
                                                    ).replace(
                                                        /\/[^/]+\.html$/,
                                                        `/${selectedVariant?.productId}.html`
                                                    )
                                                    if (driveRecipeID) {
                                                        setEngraveSwatchSpinner(true)
                                                    }
                                                    if (id === 'carat' && isEngagementProduct) {
                                                        setSelectedCarat(true)
                                                    }
                                                    if (isEditEngraving) {
                                                        newVariantUrl =
                                                            newVariantUrl + window.location.search
                                                    }
                                                    if (!newVariantUrl) {
                                                        return
                                                    }
                                                    const finalUrl = newVariantUrl
                                                        ? new URL(newVariantUrl).pathname +
                                                          (isEditEngraving
                                                              ? new URL(newVariantUrl).search
                                                              : '') +
                                                          urlHash
                                                        : updatedPath + urlHash
                                                    history.replace(finalUrl)
                                                }
                                            } finally {
                                                setTimeout(() => {
                                                    if (!suppressSwatchSpinner) {
                                                        actionSpinnerRef.current?.hide()
                                                    }
                                                }, 100)
                                            }
                                        }}
                                    >
                                        {content}
                                    </Swatch>
                                )
                            }
                        )
                        const attributeId =
                            id === 'carat' || id === 'size'
                                ? 'size'
                                : id?.toLowerCase()?.indexOf('color') !== -1
                                ? 'color'
                                : id
                        const showName =
                            (product?.c_PDPSwatches &&
                                product?.c_PDPSwatches?.indexOf(name) === -1) ||
                            attrIndex === 0
                        return (
                            <Box key={id} className="swatch-group-parent">
                                {(!product?.c_PDPSwatches || showName) && !isProductPartOfSet && (
                                    <Box
                                        {...styles.variationsStack.labelBox}
                                        className="pdp-style-name"
                                        {...(!selectedValue?.name && showOptionsMessage
                                            ? {color: 'red.950'}
                                            : {})}
                                    >
                                        {!selectedValue?.name && showOptionsMessage && (
                                            <ErrorIcon
                                                boxSize="19px"
                                                marginRight="15px"
                                                transform="translateY(2px)"
                                            />
                                        )}
                                        {isCharmAttribute
                                            ? `${name} `
                                            : id === 'size'
                                            ? 'Size '
                                            : id === 'metal'
                                            ? 'Metal '
                                            : id === 'letter'
                                            ? 'Letter'
                                            : id === 'length'
                                            ? 'Length'
                                            : id === 'carat'
                                            ? 'Carat Size'
                                            : 'Color '}{' '}
                                        <Box
                                            fontWeight="300"
                                            ml="15px"
                                            data-name={name}
                                            marginBottom={
                                                !!product?.c_PDPSwatches &&
                                                product?.c_PDPSwatches?.indexOf(name) != -1 &&
                                                attrIndex === 0
                                                    ? '30px'
                                                    : '0px'
                                            }
                                            className={
                                                id === 'size swatch-selection' ||
                                                id === 'carat swatch-selection'
                                                    ? 'swatch-selection'
                                                    : 'color-label swatch-selection'
                                            }
                                        >
                                            {!selectedValue?.name ? (
                                                <Text
                                                    as="span"
                                                    className="not-selected-attributes"
                                                    data-attribute={attributeId}
                                                >
                                                    <FormattedMessage
                                                        values={{
                                                            attributeId: isCharmAttribute
                                                                ? name
                                                                : product?.c_PDPSwatches &&
                                                                  product?.c_PDPSwatches
                                                                      ?.toLowerCase()
                                                                      ?.indexOf(attributeId) !== -1
                                                                ? 'Color'
                                                                : name
                                                        }}
                                                        defaultMessage="Select a {attributeId}"
                                                        id="product_view.select_an_attribute"
                                                    />
                                                </Text>
                                            ) : (
                                                ''
                                            )}
                                            {selectedValue?.name}
                                        </Box>
                                        {!isCart &&
                                            id === 'size' &&
                                            product?.c_sizeChart &&
                                            values.length > 1 && (
                                                <Flex
                                                    marginLeft="auto"
                                                    marginRight="10px"
                                                    alignItems="center"
                                                    className="ruler-parent"
                                                    gap="5px"
                                                >
                                                    <Image
                                                        src={getUrl('UIImages/images/ruler.svg')}
                                                        height="16px"
                                                        width="16px"
                                                        className="ruler-image"
                                                        alt="ruler"
                                                    />
                                                    <Button
                                                        onClick={onOpenSizeGuide}
                                                        className="ruler-btn"
                                                        variant="textUnderline"
                                                        height="auto"
                                                        fontSize="16px"
                                                        lineHeight="25px"
                                                        letterSpacing="0.08px"
                                                    >
                                                        {intl.formatMessage({
                                                            defaultMessage: 'Size Guide',
                                                            id: 'size.guide'
                                                        })}
                                                    </Button>
                                                </Flex>
                                            )}
                                    </Box>
                                )}
                                <SwatchGroup
                                    key={id}
                                    value={selectedValue?.value}
                                    displayName={selectedValue?.name || ''}
                                    ariaLabel={isCharmAttribute ? name : undefined}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                    pdpSwatches={product?.c_PDPSwatches}
                                    label={
                                        !isCharmAttribute &&
                                        product?.c_PDPSwatches?.indexOf(name) !== -1 &&
                                        values?.length
                                            ? name
                                            : null
                                    }
                                >
                                    {swatches}
                                </SwatchGroup>
                            </Box>
                        )
                    })
            )}
        </VStack>
    )
}

ProductViewSwatches.propTypes = {
    product: PropTypes.object,
    showLoading: PropTypes.any,
    productVariationAttributes: PropTypes.any,
    setSelectedCarat: PropTypes.any,
    isEngagementProduct: PropTypes.any,
    setProduct: PropTypes.any,
    setEngraveSwatchSpinner: PropTypes.any,
    styles: PropTypes.any,
    showOptionsMessage: PropTypes.any,
    onOpenSizeGuide: PropTypes.any,
    ispdp: PropTypes.any,
    isEditEngraving: PropTypes.any,
    isProductPartOfSet: PropTypes.any,
    driveRecipeID: PropTypes.any,
    variant: PropTypes.any,
    isCart: PropTypes.bool,
    setShouldFetchProduct: PropTypes.func,
    selectedCarat: PropTypes.any,
    suppressSwatchSpinner: PropTypes.bool
}

export default ProductViewSwatches
